/**
 * The standards catalogue, cached per `discipline:grade`.
 *
 * An author clicks between activities constantly and every activity card mounts a `StandardSelect`,
 * so without this, selecting each of 27 activities in turn would fire 27 identical requests for the
 * same ~15 KB payload.
 *
 * The cache is MODULE-SCOPED rather than a Pinia store because it is a pure function of
 * `(discipline, grade)` — a memo, not application state, carrying no document data. The accepted
 * trade-off: it outlives a page navigation and is only reset by a reload, which is right for a
 * catalogue that changes when someone runs an artisan command.
 *
 * The in-flight map is the other half of the same problem: several cards can mount in one tick, so
 * a key that is being fetched must not be fetched again — callers await the one promise.
 *
 * `useStandardLabelLookup` resolves a code against every cached catalogue (RND-98: a document may
 * tag codes from several grades).
 */
import { computed, ref, watch } from 'vue'

import type { ComputedRef } from 'vue'
import type { AssessmentStandardOption } from './interfaces'
import type { DisciplineCode } from '@brighterly/lib-core-types/student-assessment/types'
import type { Grade } from '@brighterly/lib-core-types/kid/enums'

import { useAssessmentStandardsApi } from '../../api/assessment-standards/list.hook'

type CatalogueKey = `${string}:${string}`

const cache = new Map<CatalogueKey, AssessmentStandardOption[]>()
const inFlight = new Map<CatalogueKey, Promise<AssessmentStandardOption[]>>()

/** Bumped when a fetch lands, to re-run the computeds over the non-reactive `Map`. */
const revision = ref<number>(0)

const catalogueKey = (disciplineCode: string, grade: string): CatalogueKey =>
  `${disciplineCode}:${grade}`

/** Test seam. The cache is module-scoped, so a test that fetches one key would otherwise leak into
 *  the next test's expectations. */
export const resetStandardsCatalogueCache = (): void => {
  cache.clear()
  inFlight.clear()
  revision.value += 1
}

/** A code's statement from any cached catalogue; `undefined` when none fetched so far carries it. */
export const useStandardLabelLookup = (): ComputedRef<(code: string) => string | undefined> =>
  computed<(code: string) => string | undefined>(() => {
    void revision.value
    const labels = new Map<string, string>()
    for (const standards of cache.values()) {
      for (const standard of standards) {
        if (!labels.has(standard.code)) {
          labels.set(standard.code, standard.label)
        }
      }
    }
    return (code: string) => labels.get(code)
  })

interface UseStandardsCatalogueOptions {
  /** Getters, not values: both are editable in the editor (`setGrade` / `setDisciplineCode`), and a
   *  snapshot would keep offering the old grade's standards. */
  disciplineCode: () => DisciplineCode | null
  grade: () => Grade | null
}

interface UseStandardsCatalogueReturn {
  /** The rows for the current key. Empty while loading, and empty — not stale — when the key
   *  changes, so a grade switch can never briefly offer the previous grade's standards. */
  refStandards: ComputedRef<AssessmentStandardOption[]>
  refIsLoading: ComputedRef<boolean>
}

export const useStandardsCatalogue = (
  { disciplineCode, grade }: UseStandardsCatalogueOptions,
): UseStandardsCatalogueReturn => {
  const isLoading = ref<boolean>(false)

  const refKey = computed<CatalogueKey | null>(() => {
    const discipline = disciplineCode()
    const currentGrade = grade()
    return discipline && currentGrade ? catalogueKey(discipline, currentGrade) : null
  })

  const refStandards = computed<AssessmentStandardOption[]>(() => {
    void revision.value
    const key = refKey.value
    return key ? cache.get(key) ?? [] : []
  })

  const fetchKey = async (key: CatalogueKey, discipline: DisciplineCode, currentGrade: Grade): Promise<void> => {
    if (cache.has(key)) {
      return
    }

    const pending = inFlight.get(key) ?? (() => {
      const promise = useAssessmentStandardsApi({ disciplineCode: discipline, grade: currentGrade })
        .then(standards => standards ?? [])
      inFlight.set(key, promise)
      return promise
    })()

    isLoading.value = true

    try {
      const standards = await pending
      // A failed request resolves to `[]` and is NOT cached: the toast already told the author, and
      // the next picker they open should retry rather than show an empty catalogue forever.
      if (standards.length > 0) {
        cache.set(key, standards)
      }
      revision.value += 1
    }
    finally {
      inFlight.delete(key)
      isLoading.value = false
    }
  }

  watch(refKey, (key) => {
    const discipline = disciplineCode()
    const currentGrade = grade()

    if (key && discipline && currentGrade) {
      void fetchKey(key, discipline, currentGrade)
    }
  }, { immediate: true })

  return {
    refStandards,
    refIsLoading: computed<boolean>(() => isLoading.value),
  }
}
